import React, { useState, useContext, useEffect } from "react";
import "./Reports.scss";
import { useNavigate, useLocation, data } from "react-router-dom";
import HeaderNavigate from "../../components/headerNavigate/HeaderNavigate";
import ReportTypeSelector from "../../components/customReport/ReportTypeSelector";
import ReportExportOptions from "../../components/customReport/ReportExportOptions";
import CustomReportFields from "../../components/customReport/CustomReportFields";
import { useCustomSearchParams } from "../../components/hooks/useCustomSearchParams";
import { SEARCH_PARAMS } from "../../utils/SearchParams";
import ImageContainer from "../../components/imageContainer/ImageContainer";
import AxiosService from "../../utils/AxiosService";
import { KEY_NAME } from "../../utils/KeyName";
import { AppContext } from "../../context/AppContext";
import { Modal } from "react-bootstrap";
import ExportReportModal from "../../components/Modal/ExportReportModal/ExportReportModal";
import EntityHeader from "../../components/dashboardLayout/EntityHeader/EntityHeader";

const MASTER_FIELD_ORDER = [
  "doi",
  "funder_id",
  "funder_name",
  "funder_ror",
  "author_name",
  "orcid_id",
  "article_title",
  "journal_name",
  "online_publication_date",
  "print_publication_date",
  "verified_oa_publisher_site",
  "license_type",
  "license_url",
  "reuse_license_start_date",
  "funder_portal_url",
  "grant_id",
  "archive_status",
  "datasets",
  "dataset_title",
  "dataset_type",
  "publisher",

  // unmatched items at the end
  "affiliation_name",
  "affiliation_ror",
  "volume",
  "issue",
  "pages_article_number",
  "publication_type",
  "issn",
  "first_publication_date",
  "latest_publication_date",
  "repository_name",
  "parent_funder_name",
  "parent_funder_ror",
  "funder_country",
];

const columnObj = {
  author_name: { field: "Author(s)", label: "Author name(s)" },
  orcid_id: { field: "ORCID", label: "ORCID IDs" },

  affiliation_name: { field: "", label: "" },
  affiliation_ror: { field: "RoRID (Funder)", label: "Affiliation ROR(s)" },
  funder_name: { field: "Agencies", label: "Funder Name" },
  funder_ror: { field: "RoRID (Funder)", label: "Funder ROR(s)" },
  funder_country: { field: "", label: "" },
  grant_id: { field: "Grant ID", label: "Grant ID(s)" },
  funder_portal_url: {
    field: "Agency Portal URL",
    label: "Funder portal URL(s)",
  },

  doi: { field: "DOI", label: "DOI" },
  funder_id: { field: "Funder Id", label: "Funder ID" },
  dataset_title: { field: "", label: "" },
  first_publication_date: { field: "Added", label: "First publication date" },
  latest_publication_date: {
    field: "Updated",
    label: "Latest publication date",
  },

  verified_oa_publisher_site: {
    field: "Publicly Accessible On Publisher Site",
    label: "Verified OA on publisher site",
  },
  license_type: { field: "License Type", label: "License type" },
  license_url: { field: "License URL", label: "License URL" },
  reuse_license_start_date: {
    field: "Reuse License Start Date",
    label: "Reuse License Start Date",
  },
  datasets: { field: "Datasets", label: "Datasets" },
  article_title: { field: "Article Title", label: "Article title" },
  online_publication_date: {
    field: "Online Publication Date",
    label: "Online publication date",
  },
  print_publication_date: {
    field: "Print publication date",
    label: "Print publication date",
  },
  volume: {
    field: "",
    label: "",
  },
  issue: {
    field: "",
    label: "",
  },
  pages_article_number: {
    field: "",
    label: "",
  },
  archive_status: {
    field: "Archived",
    label: "Archive status",
  },
  publication_type: {
    field: "",
    label: "",
  },
  journal_name: {
    field: "container-title",
    // field: "Journal Name",
    label: "Journal name",
  },
  issn: {
    field: "",
    label: "",
  },
  publisher: {
    field: "publisher",
    label: "Publisher Name",
  },
  dataset_type: {
    field: "",
    label: "",
  },
  repository_name: {
    field: "",
    label: "",
  },
};

function getReportList(entity, shortName) {
  switch (entity) {
    case KEY_NAME.FUNDER:
      return [
        { value: "site_all", label: "All" },
        { value: "customReport", label: "Custom Report" },
        {
          value: "author_affiliation_report",
          label: "Author Affiliation Report",
        },
        {
          value: "site_yes",
          label: "Open Access On Publisher Site Yes",
        },
        {
          value: "site_unknown",
          label: "Open Access On Publisher Site Unknown",
        },
        {
          value: "reuse_term_yes",
          label: "Reuse Terms Available Yes",
        },
        {
          value: "reuse_term_unknown",
          label: "Reuse Terms Available Unknown",
        },
        {
          value: "archive_yes",
          label: "Archive Yes",
        },
        {
          value: "archive_unknown",
          label: "Archive Unknown",
        },
        {
          value: "dataset_report",
          label: "Dataset Report",
        },
        ...(shortName === "JST"
          ? [
              {
                value: "retraction_doi_report",
                label: "Retraction DOIs Report",
              },
              {
                value: "corresponding_author_report",
                label: "Corresponding Author Report",
              },
            ]
          : []),
      ];
    case KEY_NAME.PUBLISHER:
      return [
        { value: "site_all", label: "All" },
        { value: "customReport", label: "Custom Report" },
        {
          value: "author_affiliation_report",
          label: "Author Affiliation Report",
        },
        {
          value: "site_yes",
          label: "Open Access On Publisher Site Yes",
        },
        {
          value: "site_unknown",
          label: "Open Access On Publisher Site Unknown",
        },
        {
          value: "reuse_term_yes",
          label: "Reuse Terms Available Yes",
        },
        {
          value: "reuse_term_unknown",
          label: "Reuse Terms Available Unknown",
        },
        {
          value: "archive_yes",
          label: "Archive Yes",
        },
        {
          value: "archive_unknown",
          label: "Archive Unknown",
        },
        {
          value: "funder_content_report",
          label: "Funder Content Report",
        },
        {
          value: "dataset_report",
          label: "Dataset Report",
        },
        {
          value: "retraction_doi_report",
          label: "Retraction DOIs Report",
        },
      ];
    case KEY_NAME.INSTITUTION:
      return [
        { value: "site_all", label: "All" },
        { value: "customReport", label: "Custom Report" },
        {
          value: "author_affiliation_report",
          label: "Author Affiliation Report",
        },
        {
          value: "site_yes",
          label: "Open Access On Publisher Site Yes",
        },
        {
          value: "site_unknown",
          label: "Open Access On Publisher Site Unknown",
        },
        {
          value: "reuse_term_yes",
          label: "Reuse Terms Available Yes",
        },
        {
          value: "reuse_term_unknown",
          label: "Reuse Terms Available Unknown",
        },
        {
          value: "archive_yes",
          label: "Archive Yes",
        },
        {
          value: "archive_unknown",
          label: "Archive Unknown",
        },
        {
          value: "funder_content_report",
          label: "Funder Content Report",
        },
        {
          value: "dataset_report",
          label: "Dataset Report",
        },
        {
          value: "retraction_doi_report",
          label: "Retraction DOIs Report",
        },
        {
          value: "corresponding_author_report",
          label: "Corresponding Author Report",
        },
      ];

    default:
      break;
  }
}

const Reports = () => {
  const location = useLocation();
  const { get, set } = useCustomSearchParams();
  const { entityName, metaInfo } = useContext(AppContext);

  const [shortName, setShortName] = useState(get(SEARCH_PARAMS.SHORT_NAME));
  const [reportType, setReportType] = useState(
    getReportList(entityName, shortName)?.[0],
  );
  const [fullName, setFullName] = useState(get(SEARCH_PARAMS.ENTITY_NAME));
  const [showModal, setShowModal] = useState(false);
  const [customFieldList, setCustomFieldList] = useState([]);
  const [resCode, setResCode] = useState(null);
  const [retractionErrMsg, setRetractionErrMsg] = useState(null);

  const axiosService = new AxiosService();

  // const handleActiveCustomField = (fields) => {
  //   console.log("handleActiveCustomField =>", fields);
  //   setCustomFieldList(fields.map((item) => columnObj[item]));
  // };

  const handleActiveCustomField = (fields) => {
    const sortedFields = [...fields].sort((a, b) => {
      const indexA = MASTER_FIELD_ORDER.indexOf(a);
      const indexB = MASTER_FIELD_ORDER.indexOf(b);

      const weightA = indexA === -1 ? Infinity : indexA;
      const weightB = indexB === -1 ? Infinity : indexB;

      return weightA - weightB;
    });

    setCustomFieldList(sortedFields.map((item) => columnObj[item]));
  };

  const handleExportReport = (dataObj) => {
    // console.log("DataObj =>", dataObj);
    // return;
    const queryData = { ...dataObj, reportType: reportType?.value };

    switch (entityName) {
      case KEY_NAME.FUNDER:
        if (reportType.value === "dataset_report") {
          axiosService
            .getExportReportForDatasetReport(queryData, shortName, entityName)
            .then((response) => {
              setResCode(response.status);
            })
            .catch((error) => {
              console.error("Error:", error);

              setResCode(error.status || "Network Error");
            })
            .finally(() => {});
        } else if (reportType.value === "retraction_doi_report") {
          axiosService
            .getExportReportForRetractionReport(
              queryData,
              shortName,
              entityName,
            )
            .then((response) => {
              setResCode(response.status);
            })
            .catch((error) => {
              console.error("Error:", error);
              setRetractionErrMsg(error.message);
              setResCode(error.status || "Network Error");
            })
            .finally(() => {});
        } else if (reportType.value === "corresponding_author_report") {
          axiosService
            .getExportReportForCorrespondenceReport(
              queryData,
              shortName,
              fullName,
              entityName,
            )
            .then((response) => {
              setResCode(response.status);
            })
            .catch((error) => {
              console.error("Error:", error);

              setResCode(error.status || "Network Error");
            })
            .finally(() => {});
        } else if (reportType.value === "customReport") {
          let reqBody = {
            ...queryData,
            clickedValue: 100,
            category: "site_all",
            columns: customFieldList,
          };
          axiosService
            .postFunderExportReportForOpenAccessOnPubSite(reqBody, shortName)
            .then((response) => {
              setResCode(response.status);
            })
            .catch((error) => {
              console.error("Publisher Report Error:", error);

              setResCode(error.status || "Network Error");
            })
            .finally(() => {});
        } else if (reportType.value === "author_affiliation_report") {
          let reqBody = {
            ...queryData,
            dashboardType: "agencies", //agencies or publishers
            fileType: "csv",
          };
          axiosService
            .getMailAdhocReport(reqBody, shortName)
            .then((response) => {
              setResCode(response.status);
            })
            .catch((error) => {
              console.error("Mail Adhoc Report Error:", error);

              setResCode(error.status || "Network Error");
            })
            .finally(() => {});
        } else {
          axiosService
            .getFunderExportReportForOpenAccessOnPubSite(queryData, shortName)
            .then((response) => {
              setResCode(response.status);
            })
            .catch((error) => {
              console.error("Funder Report Error:", error);

              setResCode(error.status || "Network Error");
            })
            .finally(() => {});
        }

        break;
      case KEY_NAME.PUBLISHER:
        if (reportType.value === "funder_content_report") {
          axiosService
            .getPublisherExportReportForFunderContentReport(
              queryData,
              shortName,
            )
            .then((response) => {
              setResCode(response.status);
            })
            .catch((error) => {
              console.error("Error:", error);

              setResCode(error.status || "Network Error");
            })
            .finally(() => {});
        } else if (reportType.value === "dataset_report") {
          axiosService
            .getExportReportForDatasetReport(queryData, shortName, entityName)
            .then((response) => {
              setResCode(response.status);
            })
            .catch((error) => {
              console.error("Error:", error);

              setResCode(error.status || "Network Error");
            })
            .finally(() => {});
        } else if (reportType.value === "retraction_doi_report") {
          axiosService
            .getExportReportForRetractionReport(
              queryData,
              shortName,
              entityName,
            )
            .then((response) => {
              setResCode(response.status);
            })
            .catch((error) => {
              console.error("Error:", error);
              setRetractionErrMsg(error.message);
              setResCode(error.status || "Network Error");
            })
            .finally(() => {});
        } else if (reportType.value === "customReport") {
          let reqBody = {
            ...queryData,
            clickedValue: 100,
            category: "site_all",
            columns: customFieldList,
          };
          // console.log("reqBody =>", reqBody);
          axiosService
            .postPublisherExportReportForOpenAccessOnPubSite(reqBody, shortName)
            .then((response) => {
              setResCode(response.status);
            })
            .catch((error) => {
              console.error("Publisher Report Error:", error);

              setResCode(error.status || "Network Error");
            })
            .finally(() => {});
        } else if (reportType.value === "author_affiliation_report") {
          let reqBody = {
            ...queryData,
            dashboardType: "publishers", //agencies or publishers
            fileType: "csv",
          };
          axiosService
            .getMailAdhocReport(reqBody, shortName)
            .then((response) => {
              setResCode(response.status);
            })
            .catch((error) => {
              console.error("Mail Adhoc Report Error:", error);

              setResCode(error.status || "Network Error");
            })
            .finally(() => {});
        } else {
          // console.log("PublisherExportReportForOpenAccessOnPubSite reqBody =>", queryData);
          axiosService
            .getPublisherExportReportForOpenAccessOnPubSite(
              queryData,
              shortName,
            )
            .then((response) => {
              setResCode(response.status);
            })
            .catch((error) => {
              console.error("Publisher Report Error:", error);

              setResCode(error.status || "Network Error");
            })
            .finally(() => {});
        }

        break;
      case KEY_NAME.INSTITUTION:
        if (reportType.value === "funder_content_report") {
          axiosService
            .getPublisherExportReportForFunderContentReport(
              queryData,
              shortName,
            )
            .then((response) => {
              setResCode(response.status);
            })
            .catch((error) => {
              console.error("Error:", error);

              setResCode(error.status || "Network Error");
            })
            .finally(() => {});
        } else if (reportType.value === "dataset_report") {
          axiosService
            .getExportReportForDatasetReport(queryData, shortName, entityName)
            .then((response) => {
              setResCode(response.status);
            })
            .catch((error) => {
              console.error("Error:", error);

              setResCode(error.status || "Network Error");
            })
            .finally(() => {});
        } else if (reportType.value === "retraction_doi_report") {
          axiosService
            .getExportReportForRetractionReport(
              queryData,
              shortName,
              entityName,
            )
            .then((response) => {
              setResCode(response.status);
            })
            .catch((error) => {
              console.error("Error:", error);
              setRetractionErrMsg(error.message);
              setResCode(error.status || "Network Error");
            })
            .finally(() => {});
        } else if (reportType.value === "corresponding_author_report") {
          axiosService
            .getExportReportForCorrespondenceReport(
              queryData,
              shortName,
              fullName,
              entityName,
            )
            .then((response) => {
              setResCode(response.status);
            })
            .catch((error) => {
              console.error("Error:", error);

              setResCode(error.status || "Network Error");
            })
            .finally(() => {});
        } else if (reportType.value === "customReport") {
          let reqBody = {
            ...queryData,
            clickedValue: 100,
            category: "site_all",
            columns: customFieldList,
          };
          axiosService
            .postPublisherExportReportForOpenAccessOnPubSite(reqBody, shortName)
            .then((response) => {
              setResCode(response.status);
            })
            .catch((error) => {
              console.error("Publisher Report Error:", error);

              setResCode(error.status || "Network Error");
            })
            .finally(() => {});
        } else if (reportType.value === "author_affiliation_report") {
          let reqBody = {
            ...queryData,
            dashboardType: "publishers", //agencies or publishers
            fileType: "csv",
          };
          axiosService
            .getMailAdhocReport(reqBody, shortName)
            .then((response) => {
              setResCode(response.status);
            })
            .catch((error) => {
              console.error("Mail Adhoc Report Error:", error);

              setResCode(error.status || "Network Error");
            })
            .finally(() => {});
        } else {
          axiosService
            .getInstitutionExportReportForOpenAccessOnPubSite(
              queryData,
              shortName,
            )
            .then((response) => {
              setResCode(response.status);
            })
            .catch((error) => {
              console.error("Institution Report Error:", error);

              setResCode(error.status || "Network Error");
            })
            .finally(() => {});
        }

      default:
        break;
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setResCode(null);
    setRetractionErrMsg(null);
  };

  useEffect(() => {
    if (resCode) {
      setShowModal(true);
    }
  }, [resCode]);

  useEffect(() => {
    if (entityName) {
      setReportType(getReportList(entityName, shortName)?.[0]);
    }
  }, [entityName]);

  return (
    <div className="report-container mb-5">
      <HeaderNavigate
        label={`Funder Dashboard / ${get(SEARCH_PARAMS.ENTITY_NAME)}`}
      />

      {/* HEADER */}
      <div className="bg-light text-start py-4 px-3 mb-4">
        <EntityHeader
          metaInfo={metaInfo}
          dashboardType={entityName}
          title={get(SEARCH_PARAMS.ENTITY_NAME)}
        />
      </div>

      {/* CONTENT */}
      <div className="reports-selection">
        <h3 className="section-title">Reports</h3>
        <ReportTypeSelector
          value={reportType}
          options={getReportList(entityName, shortName)}
          onChange={(value) => {
            setReportType(value);
          }}
        />
      </div>

      <hr />
      <div className="reports-content">
        {reportType?.value === "customReport" && (
          <CustomReportFields
            handleActiveCustomField={handleActiveCustomField}
          />
        )}

        <div className="report-export-option-container">
          <ReportExportOptions
            handleExportReport={handleExportReport}
            showTimeFilter={
              ![
                "dataset_report",
                "author_affiliation_report",
                "retraction_doi_report",
              ].includes(reportType?.value)
            }
            showFileType={
              [
                "site_all",
                "customReport",
                "site_yes",
                "site_unknown",
                "reuse_term_yes",
                "reuse_term_unknown",
                "archive_yes",
                "archive_unknown",
              ].includes(reportType?.value)
                ? true
                : false
            }
            // showFileType={false}
          />
        </div>
      </div>

      {showModal && (
        <ExportReportModal
          handleClose={handleModalClose}
          resCode={resCode}
          retractionErrMsg={retractionErrMsg}
        />
      )}
    </div>
  );
};

export default Reports;
